import { useNavigate } from 'react-router';
import styled from '@emotion/styled';
import { SUBSCRIPTIONS_PLANS, SUBSCRIPTIONS_HISTORY } from '../../../../routes/routeConstants';
import { useAppSelector } from '../../../../redux/store';
import { BillingHistoryItem } from '../../../../redux/slices/subscriptionSlice';
import useGetMySubscription from '../../../../hooks/dashboard/subscription/useGetMySubscription';
import useGetEntitlements from '../../../../hooks/dashboard/subscription/useGetEntitlements';
import useGetBillingHistory from '../../../../hooks/dashboard/subscription/useGetBillingHistory';
import useCancelSubscription from '../../../../hooks/dashboard/subscription/useCancelSubscription';
import useUpdateAutoRenew from '../../../../hooks/dashboard/subscription/useUpdateAutoRenew';
import { monthlyLimitLabel } from '../../../../utils/subscription';
import SubscriptionUsage from '../../../../components/Subscription/SubscriptionUsage';

const SubscriptionSettings = () => {
  const navigate = useNavigate();
  const { activeSubscription, entitlements, billingHistory, subscriptionError, entitlementsError } = useAppSelector((state) => state.subscription);

  const { getMySubscription, loading: subLoading } = useGetMySubscription();
  const { getEntitlements, loading: entLoading } = useGetEntitlements();
  const { loading: historyLoading, error: historyError } = useGetBillingHistory();
  const { cancelSubscription, loading: cancelLoading } = useCancelSubscription();
  const { updateAutoRenew, loading: autoRenewLoading } = useUpdateAutoRenew();

  const isActive = activeSubscription?.status === 'ACTIVE';
  const isCanceled = activeSubscription?.status === 'CANCELED';
  const isScheduledForCancellation = isActive && !activeSubscription?.autoRenewEnabled;
  const isPastDue = activeSubscription?.status === 'PAST_DUE' || activeSubscription?.status === 'GRACE_PERIOD';
  const isFree = activeSubscription?.status === 'FREE';

  const handleCancel = async (reason?: string) => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      await cancelSubscription(reason);
    }
  };

  const handleAutoRenewChange = async (enabled: boolean) => {
    await updateAutoRenew(enabled);
  };

  const handleUpgradeClick = () => {
    navigate(`/${SUBSCRIPTIONS_PLANS}`);
  };

  const handleBillingHistoryClick = () => {
    navigate(`/${SUBSCRIPTIONS_HISTORY}`);
  };

  if (subLoading || entLoading) {
    return (
      <Container>
        <Loading>Loading subscription settings...</Loading>
      </Container>
    );
  }

  if (subscriptionError || entitlementsError) return <Container><Card role="alert">
    <p>{subscriptionError || entitlementsError}</p>
    <button type="button" onClick={() => { void Promise.all([getMySubscription(), getEntitlements()]); }}>Try again</button>
  </Card></Container>;

  return (
    <Container>
      <Card>
        <CardHeader>
          <PlanInfo>
            <PlanName>{activeSubscription?.plan?.name || 'No active plan'}</PlanName>
            <Price>{activeSubscription ? `NGN${Number(activeSubscription.plan.amount).toLocaleString('en-NG', { maximumFractionDigits: 0 })}` : '—'}</Price>
            <Period>{activeSubscription ? `Per ${activeSubscription.plan.billingInterval === 'ANNUAL' ? 'Year' : 'Month'}` : 'Choose a plan'}</Period>
            <StatusBadge style={{ background: getStatusColor(activeSubscription?.status || 'FREE').bg, color: getStatusColor(activeSubscription?.status || 'FREE').color }}>
              {!activeSubscription ? 'No active plan' : isScheduledForCancellation ? 'Cancels at period end' : getStatusLabel(activeSubscription.status)}
            </StatusBadge>
          </PlanInfo>
          <ChangePlanButton type="button" onClick={handleUpgradeClick}>Change plan</ChangePlanButton>
        </CardHeader>

        <RecentTransactions>
          <TransactionsHeader>
            <h3>Recent transactions</h3>
            {billingHistory.length > 7 && <HistoryLink type="button" onClick={handleBillingHistoryClick}>View all</HistoryLink>}
          </TransactionsHeader>
          {historyLoading ? (
            <TransactionsMessage>Loading transactions...</TransactionsMessage>
          ) : historyError ? (
            <TransactionsMessage role="alert">{historyError}</TransactionsMessage>
          ) : billingHistory.length ? (
            <TransactionList>
              {billingHistory.slice(0, 7).map((item) => (
                <TransactionItem key={item.id}>
                  <TransactionDescription>
                    <strong>{getTransactionTitle(item)}</strong>
                    <time dateTime={item.paidAt || item.createdAt}>{formatTransactionDate(item.paidAt || item.createdAt)}</time>
                  </TransactionDescription>
                  <TransactionAmount refunded={item.status === 'REFUNDED'}>
                    {item.status === 'REFUNDED' ? '+' : ''}NGN{Number(item.amount).toLocaleString('en-NG', { maximumFractionDigits: 0 })}
                  </TransactionAmount>
                </TransactionItem>
              ))}
            </TransactionList>
          ) : (
            <TransactionsMessage>No transactions yet. Payments and refunds will appear here.</TransactionsMessage>
          )}
        </RecentTransactions>

        <Details>
          {isFree && <DetailItem><Label>Renewal</Label><Value>No paid renewal</Value></DetailItem>}
          {!isFree && activeSubscription?.startsAt && (
            <DetailItem>
              <Label>Started</Label>
              <Value>{formatDate(activeSubscription.startsAt)}</Value>
            </DetailItem>
          )}
          {!isFree && activeSubscription?.expiresAt && (
            <DetailItem>
              <Label>{isCanceled || isScheduledForCancellation ? 'Expires' : 'Renews'}</Label>
              <Value>{formatDate(activeSubscription.expiresAt)}</Value>
            </DetailItem>
          )}
          {activeSubscription?.gracePeriodEndsAt && (
            <DetailItem>
              <Label>Grace Period Ends</Label>
              <Value>{formatDate(activeSubscription.gracePeriodEndsAt)}</Value>
            </DetailItem>
          )}
          <DetailItem>
            <Label>Auto-renew</Label>
            <AutoRenewToggle
              type="checkbox"
              aria-label="Auto-renew"
              checked={activeSubscription?.autoRenewEnabled ?? false}
              onChange={(e) => handleAutoRenewChange(e.target.checked)}
              disabled={autoRenewLoading || !isActive || activeSubscription?.provider === 'manual'}
            />
          </DetailItem>
        </Details>
        <SubscriptionUsage entitlements={entitlements} />

        {entitlements && (
          <Entitlements>
            <EntitlementsTitle>Your Plan Includes:</EntitlementsTitle>
            <EntitlementsGrid>
              <EntitlementItem enabled={entitlements.canPostJobs}>
                <EntitlementIcon>📝</EntitlementIcon>
                <EntitlementText enabled={entitlements.canPostJobs}>Post Jobs ({monthlyLimitLabel(entitlements.monthlyJobPostLimit)})</EntitlementText>
              </EntitlementItem>
              <EntitlementItem enabled={entitlements.canApplyToJobs}>
                <EntitlementIcon>📄</EntitlementIcon>
                <EntitlementText enabled={entitlements.canApplyToJobs}>Apply to Jobs ({monthlyLimitLabel(entitlements.monthlyJobApplicationLimit)})</EntitlementText>
              </EntitlementItem>
              <EntitlementItem enabled={entitlements.canReceiveJobAlerts}>
                <EntitlementIcon>🔔</EntitlementIcon>
                <EntitlementText enabled={entitlements.canReceiveJobAlerts}>Job Alerts</EntitlementText>
              </EntitlementItem>
              <EntitlementItem enabled={entitlements.canReceiveJobInvites}>
                <EntitlementIcon>💌</EntitlementIcon>
                <EntitlementText enabled={entitlements.canReceiveJobInvites}>Job Invites</EntitlementText>
              </EntitlementItem>
              <EntitlementItem enabled={entitlements.canUseFindLocum}>
                <EntitlementIcon>🔍</EntitlementIcon>
                <EntitlementText enabled={entitlements.canUseFindLocum}>Find a Locum</EntitlementText>
              </EntitlementItem>
            </EntitlementsGrid>
          </Entitlements>
        )}

        <Actions>
          {isActive && (
            <>
              <ManageButton onClick={handleBillingHistoryClick}>Billing History</ManageButton>
              {isScheduledForCancellation ? (
                <UpgradeButton
                  onClick={() => handleAutoRenewChange(true)}
                  disabled={autoRenewLoading}
                >
                  {autoRenewLoading ? 'Updating...' : 'Resume Auto-renew'}
                </UpgradeButton>
              ) : (
                <CancelButton onClick={() => handleCancel()} disabled={cancelLoading}>
                  {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
                </CancelButton>
              )}
            </>
          )}
          {(isFree || !activeSubscription || isCanceled || isPastDue) && (
            <UpgradeButton onClick={handleUpgradeClick}>
              {!activeSubscription ? 'View Plans' : isFree ? 'Upgrade Now' : isCanceled ? 'Reactivate' : 'Resolve Payment'}
            </UpgradeButton>
          )}
        </Actions>
      </Card>

      <InfoCard>
        <InfoTitle>Need Help?</InfoTitle>
        <InfoText>
          If you have any questions about your subscription or billing, please{' '}
          <a href="mailto:info@locumspace.co">contact our support team</a>.
        </InfoText>
      </InfoCard>
    </Container>
  );
};

const getStatusColor = (status: string) => {
  const colors: Record<string, { label: string; color: string; bg: string }> = {
    ACTIVE: { label: 'Active', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
    PENDING_PAYMENT: { label: 'Pending Payment', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
    PAST_DUE: { label: 'Past Due', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
    GRACE_PERIOD: { label: 'Grace Period', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
    EXPIRED: { label: 'Expired', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
    CANCELED: { label: 'Canceled', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' },
    FREE: { label: 'Free Plan', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' },
  };
  return colors[status] || colors.FREE;
};

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    ACTIVE: 'Active',
    PENDING_PAYMENT: 'Pending Payment',
    PAST_DUE: 'Past Due',
    GRACE_PERIOD: 'Grace Period',
    EXPIRED: 'Expired',
    CANCELED: 'Canceled',
    FREE: 'Free Plan',
  };
  return labels[status] || 'Free Plan';
};

const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const transactionDateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formatTransactionDate = (dateString: string) => transactionDateFormatter.format(new Date(dateString));

const getTransactionTitle = (item: BillingHistoryItem) => {
  const planName = item.planName === 'Unknown Plan' ? 'Subscription' : `${item.planName} subscription`;
  if (item.status === 'REFUNDED') return `${planName} refund`;
  if (item.status === 'FAILED') return `${planName} · Failed`;
  if (item.status === 'PENDING') return `${planName} · Pending`;
  return planName;
};

export default SubscriptionSettings;

const Container = styled.div`
  width: 100%;
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  border-radius: 8px;
  padding: clamp(1.25rem, 3vw, 3rem);
  background: ${({ theme }) => theme.palette.white};
  margin-bottom: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(2rem, 8vw, 8rem);
  padding: 0 0 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  @media (max-width: 600px) { justify-content: space-between; gap: 1rem; }
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const PlanName = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.greyGrey1};
  text-transform: uppercase;
`;

const StatusBadge = styled.span<{ style?: React.CSSProperties }>`
  margin-top: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const Price = styled.div`
  margin-top: 0.25rem;
  font-size: clamp(1.7rem, 2.4vw, 2.25rem);
  font-weight: 700;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const Period = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.greyGrey2};
`;

const ChangePlanButton = styled.button`
  border: 1px solid ${({ theme }) => theme.palette.greyGrey2};
  border-radius: 999px;
  padding: 0.65rem 1.5rem;
  color: ${({ theme }) => theme.palette.greyGrey1};
  background: ${({ theme }) => theme.palette.white};
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.palette.mainBlue}; color: ${({ theme }) => theme.palette.mainBlue}; }
  &:focus-visible { outline: 3px solid ${({ theme }) => theme.palette.mainBlue}; outline-offset: 3px; }
`;

const RecentTransactions = styled.section`
  padding: 2rem 0;
`;

const TransactionsHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 1rem;
  h3 { margin: 0 0 1.25rem; font-size: 1.4rem; color: ${({ theme }) => theme.palette.greyGrey1}; }
`;

const HistoryLink = styled.button`
  border: 0; background: transparent; color: ${({ theme }) => theme.palette.mainBlue};
  font-weight: 600; cursor: pointer;
  &:focus-visible { outline: 3px solid ${({ theme }) => theme.palette.mainBlue}; }
`;

const TransactionList = styled.ul`
  display: grid; gap: 1.35rem; margin: 0; padding: 0; list-style: none;
`;

const TransactionItem = styled.li`
  display: flex; justify-content: space-between; align-items: center; gap: 1.5rem;
`;

const TransactionDescription = styled.div`
  display: flex; flex-direction: column; gap: 0.15rem; min-width: 0;
  strong { color: ${({ theme }) => theme.palette.greyGrey1}; font-size: 1rem; }
  time { color: ${({ theme }) => theme.palette.greyGrey3}; font-size: 0.8rem; }
`;

const TransactionAmount = styled.span<{ refunded: boolean }>`
  flex: none; color: ${({ theme, refunded }) => refunded ? theme.palette.mainBlue : theme.palette.stateColorRed};
  font-weight: 700; font-size: 0.9rem;
`;

const TransactionsMessage = styled.p`
  margin: 0; color: ${({ theme }) => theme.palette.greyGrey3}; font-size: 0.9rem;
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  padding: 1rem 0;
  border-top: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.greyGrey3};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Value = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const AutoRenewToggle = styled.input`
  width: 44px;
  height: 24px;
  appearance: none;
  border-radius: 12px;
  background: ${({ theme, disabled, checked }) => 
    disabled ? theme.palette.greyGrey5 : 
    checked ? theme.palette.mainBlue : theme.palette.greyGrey5};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  position: relative;
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ checked }) => checked ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s ease;
  }
`;

const Entitlements = styled.div`
  margin-bottom: 1.5rem;
`;

const EntitlementsTitle = styled.h4`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

const EntitlementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
`;

const EntitlementItem = styled.div<{ enabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  background: ${({ theme, enabled }) => (enabled ? 'rgba(16, 185, 129, 0.05)' : 'rgba(107, 114, 128, 0.05)')};
  border: 1px solid ${({ theme, enabled }) => (enabled ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)')};
  opacity: ${({ enabled }) => (enabled ? 1 : 0.6)};
`;

const EntitlementIcon = styled.span`
  font-size: 1rem;
`;

const EntitlementText = styled.span<{ enabled: boolean }>`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme, enabled }) => (enabled ? theme.palette.stateColorGreen : theme.palette.greyGrey3)};
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const ManageButton = styled.button`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.palette.mainBlue};
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.mainBlue};
  background: ${({ theme }) => theme.palette.white};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(37, 99, 235, 0.05);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.palette.stateColorRed};
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.stateColorRed};
  background: ${({ theme }) => theme.palette.white};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const UpgradeButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.white};
  background: ${({ theme }) => theme.palette.mainBlue};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const InfoCard = styled.div`
  padding: 1rem;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.mainBlue}10;
  border: 1px solid ${({ theme }) => theme.palette.mainBlue}30;
`;

const InfoTitle = styled.h4`
  margin: 0 0 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.mainBlue};
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.greyGrey2};
  line-height: 1.5;

  a {
    color: ${({ theme }) => theme.palette.mainBlue};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Loading = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.palette.greyGrey3};
`;
